// path: frontend/src/components/Modals/VacationModal.jsx
/**
 * Отпуск: создание / редактирование / просмотр.
 *
 * RBAC-флаги:
 *   - capabilities.manageAllVacations — «Сотрудник» (селектор для
 *     оформления чужого отпуска) и статус «Утверждён» для нового;
 *   - vacation.permissions.canManage — редактирование/удаление
 *     (сервер: canManageVacation: admin/director/hr или owner в pending);
 *   - vacation.permissions.canApprove — статус «Утверждён» для pending
 *     (сервер: canApproveVacation).
 *
 * Для новой записи permissions ещё нет — считаем canManageFallback =
 * capabilities.manageAllVacations || forEmpId === ur.id (совпадает с
 * VacationService.create: admin/director/hr или self). Сервер всё
 * равно проверит.
 */
import { useCallback, useMemo } from "react";
import { ModalShell } from "../ModalShell";
import { FormField } from "../FormField";
import { useConfirm } from "../../context/ConfirmContext";
import { useDataHelpers } from "../../hooks/useDataHelpers";
import { useAuth } from "../../hooks/useAuth";
import { useForm } from "../../hooks/useForm";
import { useAsyncSubmit } from "../../hooks/useAsyncSubmit";
import { VACATION_TYPES } from "../../utils/constants";
import { getPrimaryDeptName } from "../../utils/helpers";
import { TODAY, addDays, iso, uid, fmtDMY } from "../../utils/date";
import { ICONS } from "../iconsData";
import { Ic } from "../Icons";

const FIELDS = Object.freeze([
  "empId",
  "start",
  "end",
  "type",
  "comment",
  "status",
]);

export function VacationModal({
  db,
  ur,
  vacationId,
  forEmpId,
  onClose,
  onSave,
  onDelete,
  toast,
}) {
  const { capabilities } = useAuth();
  const { empName } = useDataHelpers(db);
  const { confirm } = useConfirm();
  const existing = vacationId
    ? db.vacations.find((v) => v.id === vacationId)
    : null;
  const isNew = !existing;
  const canPick = Boolean(capabilities.manageAllVacations);
  const isRejected = existing?.status === "rejected";
  const isApproved = existing?.status === "approved";

  const initialValues = useMemo(
    () =>
      existing
        ? { ...existing }
        : {
            id: "v_" + uid(),
            empId: forEmpId || ur.id,
            start: TODAY,
            end: iso(addDays(new Date(), 7)),
            type: "annual",
            comment: "",
            status: canPick && forEmpId ? "approved" : "pending",
          },
    [existing, forEmpId, ur.id, canPick],
  );

  const validate = useCallback(
    (values) => {
      const errors = {};
      if (!values.start) errors.start = "Дата начала обязательна";
      if (!values.end) errors.end = "Дата окончания обязательна";
      if (values.end && values.start && values.end < values.start) {
        errors.end = "Дата окончания должна быть позже начала";
      }
      if (values.empId) {
        if (
          db.vacations.some(
            (v) =>
              v.empId === values.empId &&
              v.id !== values.id &&
              v.status === "approved" &&
              v.start <= values.end &&
              v.end >= values.start,
          )
        ) {
          errors.start =
            "У сотрудника уже есть утверждённый отпуск в этот период";
        }
      }
      return errors;
    },
    [db],
  );

  const { values, handleChange, handleSubmit, errors, touched } = useForm(
    initialValues,
    validate,
    { fields: FIELDS },
  );

  const targetEmpId = existing ? existing.empId : values.empId;
  const canManage = existing
    ? Boolean(existing.permissions?.canManage)
    : canPick || targetEmpId === ur.id;
  const readOnly = !!existing && !canManage;

  const { submit: save, isSubmitting } = useAsyncSubmit(
    useCallback(
      async (vals) => {
        await onSave(vals, isNew);
      },
      [onSave, isNew],
    ),
    (error) => {
      toast(error.message || "Ошибка сохранения отпуска", "error");
    },
  );

  const handleDelete = useCallback(async () => {
    if (!existing || !onDelete) return;
    if (
      !(await confirm({
        title: "Удалить отпуск",
        message: `Удалить отпуск ${fmtDMY(existing.start)}–${fmtDMY(existing.end)}?`,
        confirmLabel: "Удалить",
        danger: true,
      }))
    )
      return;
    try {
      await onDelete(existing.id);
      onClose();
    } catch (error) {
      toast(error.message || "Ошибка удаления отпуска", "error");
    }
  }, [existing, onDelete, confirm, onClose, toast]);

  const employeeOptions = db.employees.map((e) => ({
    value: e.id,
    label: `${empName(e.id)} - ${getPrimaryDeptName(e, db)}`,
  }));

  const canApproveExisting = Boolean(existing?.permissions?.canApprove);

  const statusOptions = (() => {
    if (isRejected) return [];
    if (isNew) {
      const opts = [{ value: "pending", label: "На утверждении" }];
      if (canPick && forEmpId)
        opts.push({ value: "approved", label: "Утверждён" });
      return opts;
    }
    const opts = [{ value: "pending", label: "На утверждении" }];
    if (canApproveExisting)
      opts.push({ value: "approved", label: "Утверждён" });
    return opts;
  })();

  const typeOptions = Object.entries(VACATION_TYPES).map(([k, v]) => ({
    value: k,
    label: v,
  }));

  const canDelete = !!existing && !!onDelete && canManage;
  const showStatusField = !readOnly && statusOptions.length > 1;

  const title = readOnly
    ? "Просмотр отпуска"
    : existing
      ? "Редактирование отпуска"
      : "Новый отпуск";

  const readOnlyFooter = (
    <div className="modal-foot">
      <div className="spacer" />
      <button type="button" className="btn ghost" onClick={onClose}>
        Закрыть
      </button>
    </div>
  );

  return (
    <ModalShell
      title={title}
      onClose={onClose}
      onSave={readOnly ? undefined : handleSubmit(save)}
      saveLabel="Сохранить"
      width={560}
      className="modal-vacation"
      saveDisabled={isSubmitting}
      footer={readOnly ? readOnlyFooter : null}
      showSave={!readOnly}
      actions={
        canDelete ? (
          <button
            type="button"
            className="btn danger"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            <Ic d={ICONS.trash} size={14} /> Удалить
          </button>
        ) : null
      }
    >
      {readOnly && (
        <div className="info-box">
          {isApproved
            ? "Утверждённый отпуск нельзя редактировать или удалить. Изменения вносит администратор или генеральный директор."
            : "Изменение этого отпуска недоступно для вашей роли."}
        </div>
      )}

      <div className="project-info-fields">
        {canPick && (
          <FormField
            label="Сотрудник"
            required
            type="select"
            options={employeeOptions}
            value={values.empId}
            onChange={(v) => handleChange("empId", v)}
            disabled={!!existing}
          />
        )}
        <FormField
          label="Дата начала"
          required
          type="date"
          value={values.start}
          onChange={(v) => handleChange("start", v)}
          error={touched.start && errors.start}
          disabled={readOnly}
        />
        <FormField
          label="Дата окончания"
          required
          type="date"
          value={values.end}
          onChange={(v) => handleChange("end", v)}
          error={touched.end && errors.end}
          disabled={readOnly}
        />
        <FormField
          label="Тип отпуска"
          required
          type="select"
          options={typeOptions}
          value={values.type}
          onChange={(v) => handleChange("type", v)}
          disabled={readOnly}
        />
        <FormField
          label="Комментарий"
          value={values.comment}
          onChange={(v) => handleChange("comment", v)}
          disabled={readOnly}
        />
        {showStatusField && (
          <FormField
            label="Статус"
            type="select"
            options={statusOptions}
            value={values.status}
            onChange={(v) => handleChange("status", v)}
          />
        )}
        {isRejected && (
          <div className="info-box">
            <div>
              Отпуск отклонён.
              {existing.rejectionReason &&
                ` Причина: ${existing.rejectionReason}`}
            </div>
            <div className="mut sm mt-1">
              Отклонение принимается в разделе «Запросы и заявки». Переоткрыть
              отпуск через форму нельзя.
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

export default VacationModal;
